import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface MembershipStatus {
  loading: boolean;
  hasMembership: boolean;
  startedAt: Date | null;
  expiresAt: Date | null;
  isActive: boolean;
  isExpired: boolean;
  daysUntilExpiry: number | null;
}

const EMPTY: MembershipStatus = {
  loading: true,
  hasMembership: false,
  startedAt: null,
  expiresAt: null,
  isActive: false,
  isExpired: false,
  daysUntilExpiry: null,
};

/**
 * Returns the current user's WCC360 membership/result-access status.
 * Access is granted for 12 months from successful payment.
 */
export function useMembership(): MembershipStatus {
  const { user } = useAuth();
  const [state, setState] = useState<MembershipStatus>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setState({ ...EMPTY, loading: false });
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("memberships")
        .select("started_at, expires_at")
        .eq("user_id", user.id)
        .order("expires_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled) return;

      if (!data) {
        setState({ ...EMPTY, loading: false });
        return;
      }
      const startedAt = data.started_at ? new Date(data.started_at) : null;
      const expiresAt = data.expires_at ? new Date(data.expires_at) : null;
      const now = Date.now();
      const isExpired = !!expiresAt && expiresAt.getTime() < now;
      const daysUntilExpiry = expiresAt
        ? Math.ceil((expiresAt.getTime() - now) / (1000 * 60 * 60 * 24))
        : null;
      setState({
        loading: false,
        hasMembership: true,
        startedAt,
        expiresAt,
        isActive: !isExpired,
        isExpired,
        daysUntilExpiry,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return state;
}

export function formatExpiryDate(d: Date | null): string {
  if (!d) return "—";
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}
